import { useEffect, useState, type ReactNode } from 'react'
import { useAuth, useDistributionPrincipal } from '@dss/auth'
import { ApiError } from '@dss/api-client'
import { Button } from '@dss/design-system'
import { useLocation } from '../lib/router'
import { usePortalSession } from '../features/invitations/usePortalMembership'
import { distributorWorkspacePath } from '../lib/distributorWorkspace'

const PUBLIC_PREFIXES = ['/login', '/forgot-password', '/logout', '/logout-confirm',
  '/auth/callback', '/group-invite', '/employee-invite', '/owner-invite'] as const
export function isPublicClientRoute(pathname: string) {
  return PUBLIC_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

/** JWT удостоверяет только identity; выбор рабочего места — по текущему DB membership. */
export function RoleGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, isSigningOut } = useAuth()
  const principal = useDistributionPrincipal()
  const { pathname } = useLocation()
  const isPublic = isPublicClientRoute(pathname)
  const session = usePortalSession(!isPublic && isAuthenticated && !isLoading && !isSigningOut)
  const role = !session.isError ? session.data?.role : undefined
  const isClient = role === 'CLIENT_OWNER' || role === 'CLIENT_EMPLOYEE'
  const identity = `${principal?.userId}:${principal?.distributorId}:${principal?.clientId}`
  const allowed = isClient && session.data?.state === 'ACTIVE' && !session.isError && !session.isPending
    && isAuthenticated && !isLoading && !isSigningOut
  const retryable = session.isPending || session.isError && !(session.error instanceof ApiError
    && [401, 403, 404].includes(session.error.status))
  const temporaryFailure = session.isError && retryable
  const [admitted, setAdmitted] = useState<string | null>(null)
  useEffect(() => {
    if (allowed) setAdmitted(identity)
    else if (!retryable || !isAuthenticated || isSigningOut) setAdmitted(null)
  }, [allowed, identity, retryable, isAuthenticated, isSigningOut])
  const retain = admitted === identity && retryable && isAuthenticated && !isSigningOut
  const platformTarget = import.meta.env.VITE_PLATFORM_ADMIN_URL ?? 'https://admin.ds2b.ru/admin/'
  const target = principal?.isPlatformAdmin ? platformTarget
    : distributorWorkspacePath(role)
  useEffect(() => {
    if (!isPublic && isAuthenticated && !isLoading && !isSigningOut && target) {
      window.location.assign(target)
    }
  }, [isPublic, isAuthenticated, isLoading, isSigningOut, target])
  if (isPublic || !isAuthenticated && !isLoading) return <>{children}</>
  return <>
    <div key={identity} hidden={!allowed}>
      {(allowed || retain) && <fieldset disabled={!allowed} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>{children}</fieldset>}
    </div>
    {!allowed && (isLoading || isSigningOut || session.isPending && !principal?.isPlatformAdmin
      ? <div role="status">Проверяем текущие права доступа…</div>
      : <main className="group-invitation-page" data-testid="role-gate-blocked">
    <h1>{target ? 'Открываем рабочее место дистрибьютора'
      : temporaryFailure ? 'Не удалось проверить доступ' : 'Доступ к клиентскому порталу не подтверждён'}</h1>
    <p role="alert">{target ? 'Ваша текущая роль относится к другому рабочему месту.'
      : temporaryFailure ? 'Сейчас портал не может проверить ваши права. Попробуйте ещё раз. До успешной проверки данные скрыты.'
        : 'Клиентский доступ ещё не предоставлен или был отключён. Обратитесь к тому, кто вас пригласил.'}</p>
    {!target && <Button disabled={session.isFetching} onClick={() => void session.refetch()}>Проверить доступ снова</Button>}
    {target && <p><a href={target}>Перейти в административное рабочее место</a></p>}
    {!target && !temporaryFailure && <p>Если вы получили приглашение, откройте исходную ссылку и подтвердите его.</p>}
  </main>)}
  </>
}
